import { useState } from "react";
import { MapPin, Phone, Mail, Clock, FileText, MessageSquare } from "lucide-react";
import { Seo } from "@/components/layout/Seo";
import { PageHero } from "@/components/layout/PageHero";
import { Reveal } from "@/components/motion/Reveal";
import { DevisForm } from "@/components/forms/DevisForm";
import { ContactForm } from "@/components/forms/ContactForm";
import { SITE } from "@/config/site";

const { lat, lng } = SITE.address;
const bbox = `${lng - 0.012}%2C${lat - 0.006}%2C${lng + 0.012}%2C${lat + 0.006}`;
const OSM_URL = `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${lat}%2C${lng}`;

export default function Contact() {
  const [tab, setTab] = useState("devis");
  return (
    <>
      <Seo
        title="Devis gratuit — Contact | O'RIZON 360"
        description="Demandez un devis gratuit pour vos travaux de rénovation, maçonnerie, étanchéité ou isolation en Île-de-France. Réponse sous 48 h ouvrées. 07 58 48 12 47."
      />
      <PageHero crumb="Contact" eyebrow="Contact & devis" lines={["Parlons de", "votre projet"]} intro="Devis gratuit, détaillé et sans engagement, sous 48 heures ouvrées. Décrivez-nous vos travaux, joignez des photos : nous revenons vers vous rapidement." />

      <section className="section bg-concrete">
        <div className="container-x grid lg:grid-cols-12 gap-10">
          <aside className="lg:col-span-4 space-y-6">
            <Reveal className="card p-7" >
              <h2 className="font-heading font-bold uppercase text-2xl text-slate mb-6">Coordonnées</h2>
              <ul className="space-y-5 text-ink/85">
                <li className="flex gap-3"><MapPin className="text-copper shrink-0" size={20} /><address className="not-italic" data-testid="contact-address">{SITE.address.street}<br />{SITE.address.postalCode} {SITE.address.city}, {SITE.address.country}</address></li>
                <li className="flex gap-3"><Phone className="text-copper shrink-0" size={20} /><a href={SITE.phone.href} className="font-semibold hover:text-copper transition-colors" data-testid="contact-phone-link">{SITE.phone.display}<span className="block text-xs font-normal text-muted">{SITE.phone.international}</span></a></li>
                <li className="flex gap-3"><Mail className="text-copper shrink-0" size={20} /><a href={`mailto:${SITE.email}`} className="font-semibold hover:text-copper transition-colors" data-testid="contact-email-link">{SITE.email}</a></li>
                <li className="flex gap-3"><Clock className="text-copper shrink-0" size={20} /><ul className="space-y-1 text-sm" data-testid="contact-hours">{SITE.hours.map((h) => <li key={h.days}><span className="font-semibold text-slate">{h.days} :</span> {h.hours}</li>)}</ul></li>
              </ul>
              <p className="mt-6 pt-6 border-t border-line text-sm text-muted">Zone d'intervention : Île-de-France — détaillée sur la page <a href="/services#zone-intervention" className="text-copper underline underline-offset-2">Zone d'intervention</a>.</p>
            </Reveal>
            <Reveal delay={0.1} className="card overflow-hidden">
              <iframe
                title="Carte — 8 Rue Lequesne, 94130 Nogent-sur-Marne"
                src={OSM_URL}
                className="w-full h-72 border-0"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                data-testid="contact-map"
              />
              <p className="px-5 py-3 text-xs text-muted">Carte OpenStreetMap (sans cookie tiers). <a href={`https://www.openstreetmap.org/?mlat=${lat}&mlon=${lng}#map=17/${lat}/${lng}`} target="_blank" rel="noopener noreferrer" className="text-copper underline">Agrandir</a></p>
            </Reveal>
          </aside>

          <div className="lg:col-span-8">
            <Reveal className="card p-6 sm:p-10">
              <div className="flex gap-2 p-1 bg-concrete rounded-full w-fit mb-8" role="tablist" aria-label="Type de demande">
                <button role="tab" aria-selected={tab === "devis"} onClick={() => setTab("devis")} className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-colors ${tab === "devis" ? "bg-slate text-white" : "text-slate hover:text-copper"}`} data-testid="tab-devis"><FileText size={16} /> Demande de devis</button>
                <button role="tab" aria-selected={tab === "contact"} onClick={() => setTab("contact")} className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-colors ${tab === "contact" ? "bg-slate text-white" : "text-slate hover:text-copper"}`} data-testid="tab-contact"><MessageSquare size={16} /> Contact simple</button>
              </div>
              {tab === "devis" ? (
                <>
                  <h2 className="h-display text-3xl sm:text-4xl mb-2">Demande de devis gratuit</h2>
                  <p className="text-muted mb-8">Les champs marqués d'un <span className="text-copper">*</span> sont obligatoires.</p>
                  <DevisForm />
                </>
              ) : (
                <>
                  <h2 className="h-display text-3xl sm:text-4xl mb-2">Nous écrire</h2>
                  <p className="text-muted mb-8">Une question, une demande d'attestation, un partenariat ? Écrivez-nous.</p>
                  <ContactForm />
                </>
              )}
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
